import { useMemo, useEffect, useState } from 'react'
import { createArrowPath, getArrowPosition } from '../CameraTrajectoryOverlay'
import { useAppStore } from '@store/appStore'
import { calculateFrameSequenceTiming, getAnimationConfig } from '@/config/animationConfig'
import { getDoubleClickAnimationService } from '@/services/frameAnimationService'
import { queryOutsideFrameCandidate } from '@/utils/directionNavigation'

/**
 * 🧭 NAVIGATION HINT OVERLAY
 * 
 * Completely independent hint controller - ALWAYS VISIBLE
 * - Never hidden by other overlays
 * - Never conditional on store state
 * - Renders in its own fixed container
 * - Handles all hint interactions
 */

// Direction navigation utilities (DIRECTION_ANGLES, CONE_HALF_ANGLE, normalizeAngle,
// findCandidatesInZone, queryOutsideFrameCandidate) are imported from @/utils/directionNavigation

/**
 * Handle arrow button click - find outside-frame candidates, select best one, and animate to it
 * Animation pattern follows CameraTrajectoryOverlay's handleCircleInteraction
 */
function handleArrowClick(direction: 'north' | 'south' | 'east' | 'west' | 'forward' | 'backward') {
  const ENABLE_ANIMATION = false // TODO: Set to true to re-enable sequential frame animation
  
  const appState = useAppStore.getState()
  
  // Only handle N/S/E/W directions
  if (!['north', 'south', 'east', 'west'].includes(direction)) return
  
  const typedDirection = direction as 'north' | 'south' | 'east' | 'west'

  // Query candidates in this direction
  const result = queryOutsideFrameCandidate(
    typedDirection,
    appState.manuallySelectedFrameIndex,
    appState.frameWorldPositions,
    appState.imageDimensions
  )

  // Store all candidates for visual highlighting
  appState.setCandidateFrames(result.candidateFrames)
  appState.setHighlightedZoneDirection(typedDirection)
  
  // Navigate to selected frame if one was found
  if (result.nearest === null) {
    console.log(`🎯 [ArrowNavigate] No candidates found in direction: ${typedDirection}`)
    return
  }

  const currentFrame = appState.manuallySelectedFrameIndex
  const targetFrame = result.nearest

  // If clicking current frame or no current frame, just center it
  if (currentFrame === null || currentFrame === targetFrame) {
    console.log(`🎯 [ArrowNavigate] Direct jump to frame ${targetFrame}`)
    appState.setManuallySelectedFrame(targetFrame)
    appState.centerViewportOnFrame(targetFrame)
    return
  }

  if (ENABLE_ANIMATION) {
    // === ANIMATION PATH (DISABLED) ===
    const timing = calculateFrameSequenceTiming(currentFrame, targetFrame)
    const frameCount = Math.abs(targetFrame - currentFrame)

    console.log(`🎬 [ArrowNavigate] Animation: ${currentFrame} → ${targetFrame} (${frameCount} frames)`)
    console.log(`   Timing: ${timing.frameDuration}ms per frame, ${timing.framesToShow.length} frames shown, ~${timing.totalDuration}ms total`)

    appState.setFrameSequenceAnimating(true)
    appState.setFrameSequenceAnimationTarget(targetFrame)
    appState.setAnimatingFrameIndex(timing.framesToShow[0])
    appState.setAnimationTrigger('DOUBLE_CLICK_ANIMATE')

    const animationService = getDoubleClickAnimationService()
    const config = getAnimationConfig('DOUBLE_CLICK_ANIMATE')

    if (config && timing.framesToShow.length > 0) {
      const dynamicConfig = { ...config, frameDuration: timing.frameDuration }

      const cancelFn = animationService.animateFrameSequence(
        timing.framesToShow,
        targetFrame,
        dynamicConfig,
        {
          onFrameShow: (frameIdx: number) => {
            console.log(`🎬 [ArrowNavigate] Showing frame: ${frameIdx}`)
            const freshState = useAppStore.getState()
            freshState.setAnimatingFrameIndex(frameIdx)
            freshState.centerViewportOnFrame(frameIdx, undefined, undefined, { skipAutoSelect: true })
          },
          onComplete: (finalFrame: number) => {
            console.log(`✅ [ArrowNavigate] Animation complete, selecting frame: ${finalFrame}`)
            const freshState = useAppStore.getState()

            freshState.setAnimatingFrameIndex(null)
            freshState.setAnimationTrigger(null)
            freshState.setFrameSequenceAnimating(false)
            freshState.setFrameSequenceAnimationCancel(null)
            freshState.setFrameSequenceAnimationTarget(null)

            freshState.setCandidateFrames([])
            freshState.setHighlightedZoneDirection(null)

            freshState.setManuallySelectedFrame(finalFrame)
            freshState.centerViewportOnFrame(finalFrame)
          }
        }
      )

      appState.setFrameSequenceAnimationCancel(cancelFn)
    }
  } else {
    // === INSTANT JUMP (CURRENT) ===
    const frameCount = Math.abs(targetFrame - currentFrame)
    console.log(`🎯 [ArrowNavigate] Instant jump: ${currentFrame} → ${targetFrame} (${frameCount} frames apart)`)
    
    // Clear candidate highlighting
    appState.setCandidateFrames([])
    appState.setHighlightedZoneDirection(null)
    
    // Jump to target frame
    appState.setManuallySelectedFrame(targetFrame)
    appState.centerViewportOnFrame(targetFrame)
  }
}

export function NavigationHintOverlay() {
  const isFrameSequenceAnimating = useAppStore((state) => state.isFrameSequenceAnimating)
  const panFailureDirection = useAppStore((state) => state.panFailureDirection)
  const manuallySelectedFrameIndex = useAppStore((state) => state.manuallySelectedFrameIndex)
  const frameWorldPositions = useAppStore((state) => state.frameWorldPositions)
  const imageDimensions = useAppStore((state) => state.imageDimensions)
  const panModeEnabled = useAppStore((state) => state.panModeEnabled)
  const exploreMode = useAppStore((state) => state.exploreMode)
  const canUseTapIndicator = useAppStore((state) => state.canUseTapIndicator)
  const [animatingDirection, setAnimatingDirection] = useState<'north' | 'south' | 'east' | 'west' | null>(null)

  // Check which directions have available frames whenever selection changes
  useEffect(() => {
    if (manuallySelectedFrameIndex === null || !imageDimensions || frameWorldPositions.length === 0) {
      useAppStore.getState().setAvailableDirections(['north', 'south', 'east', 'west'])
      return
    }

    const availableDirs: ('north' | 'south' | 'east' | 'west')[] = []
    const arrowCandidates: number[] = []
    const directions = ['north', 'south', 'east', 'west'] as const

    for (const dir of directions) {
      const result = queryOutsideFrameCandidate(
        dir,
        manuallySelectedFrameIndex,
        frameWorldPositions,
        imageDimensions
      )
      if (result.candidateFrames.length > 0) {
        availableDirs.push(dir)
        if (result.nearest !== null) {
          arrowCandidates.push(result.nearest)
        }
      }
    }

    console.log(`🧭 [NavigationHintOverlay] Available directions for frame ${manuallySelectedFrameIndex}:`, availableDirs, 'candidates:', arrowCandidates)
    const store = useAppStore.getState()
    store.setAvailableDirections(availableDirs)
    store.setArrowCandidateFrames(arrowCandidates)
  }, [manuallySelectedFrameIndex, frameWorldPositions, imageDimensions])

  // Watch pan failure direction and animate arrow
  useEffect(() => {
    console.log('🎯 [NavigationHintOverlay] Pan failure direction changed:', panFailureDirection)
    if (panFailureDirection) {
      const dirMap: Record<string, 'north' | 'south' | 'east' | 'west'> = {
        'up': 'north',
        'down': 'south',
        'left': 'west',
        'right': 'east'
      }
      const hintDir = dirMap[panFailureDirection]
      console.log('🎯 [NavigationHintOverlay] Mapped to hint direction:', hintDir, 'animating now')
      if (hintDir) {
        setAnimatingDirection(hintDir)
        setTimeout(() => setAnimatingDirection(null), 3000)
      }
    }
  }, [panFailureDirection])

  // Memoize directions to prevent unnecessary re-renders
  // NOTE: 'forward' and 'backward' (zoom arrows) are disabled and kept for future use
  const directions = useMemo(() => ['north', 'south', 'east', 'west', 'forward', 'backward'] as const, [])
  const enabledDirections = useMemo(() => ['north', 'south', 'east', 'west'] as const, [])
  const availableDirections = useAppStore((state) => state.availableDirections)

  return (
    <div
      data-navigation-hint-overlay
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 9999, // High z-index to always be visible
      }}
    >
      {directions.map((dir) => {
        // Disable forward/backward (zoom) arrows - kept for future implementation
        const isDisabled = !enabledDirections.includes(dir as any)
        // Check if direction has available frames
        const isAvailable = availableDirections.includes(dir as any)
        
        return (
          <button
            key={dir}
            data-hint-direction={dir}
            aria-label={`Navigate ${dir}`}
            onClick={() => handleArrowClick(dir)}
            disabled={isFrameSequenceAnimating || isDisabled}
            style={{
              ...getArrowPosition(dir),
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: isDisabled || !isAvailable ? 'rgba(128, 128, 128, 0.1)' : 'rgba(255, 255, 255, 0.15)',
              border: isDisabled || !isAvailable ? '1.5px solid rgba(128, 128, 128, 0.2)' : '1.5px solid rgba(255, 255, 255, 0.4)',
              backdropFilter: 'blur(10px)',
              opacity: isDisabled || !isAvailable ? 0.3 : 0.6,
              cursor: isFrameSequenceAnimating || isDisabled ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: isDisabled || !isAvailable ? 'none' : 'auto',
              padding: 0,
              transition: 'opacity 0.2s ease',
              position: 'fixed', // Fixed positioning for each button
              zIndex: 9999,
              animation: animatingDirection === dir && isAvailable ? `arrowBounce${dir.charAt(0).toUpperCase() + dir.slice(1)} 1.75s linear 1` : 'none',
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              style={{
                strokeWidth: '2',
                stroke: isDisabled || !isAvailable ? 'rgba(128, 128, 128, 0.4)' : 'rgba(255, 255, 255, 0.8)',
                fill: 'none',
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
              }}
            >
              <path d={createArrowPath(dir)} />
            </svg>
          </button>
        )
      })}
      {/* Explore mode toggle button (hand icon) — only shown when 3D data is available */}
      {canUseTapIndicator && (
      <button
        aria-label={exploreMode ? 'Exit explore mode' : 'Enter explore mode'}
        onClick={() => useAppStore.getState().setExploreMode(!exploreMode)}
        style={{
          position: 'fixed',
          bottom: '100px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          background: exploreMode ? 'rgba(255, 180, 0, 0.6)' : 'rgba(255, 255, 255, 0.15)',
          border: exploreMode ? '2px solid rgba(255, 200, 50, 0.8)' : '1.5px solid rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(10px)',
          opacity: exploreMode ? 0.9 : 0.6,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'auto',
          padding: 0,
          transition: 'all 0.2s ease',
          zIndex: 9999,
        }}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          style={{
            fill: 'none',
            stroke: exploreMode ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.7)',
            strokeWidth: '1.8',
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
          }}
        >
          {/* Hand/palm icon */}
          <path d="M18 11V6a2 2 0 0 0-4 0v1" />
          <path d="M14 10V4a2 2 0 0 0-4 0v2" />
          <path d="M10 10.5V6a2 2 0 0 0-4 0v8" />
          <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
        </svg>
      </button>
      )}
    </div>
  )
}

// CSS animations: 3 cycles with constant speed. 56px in 1s, 28px in 0.5s, 14px in 0.25s (total 1.75s)
const style = document.createElement('style')
style.textContent = `
  @keyframes arrowBounceNorth {
    0% { transform: translateX(-50%) translateY(0); }
    28.57% { transform: translateX(-50%) translateY(-56px); }
    57.14% { transform: translateX(-50%) translateY(0); }
    71.43% { transform: translateX(-50%) translateY(-28px); }
    85.71% { transform: translateX(-50%) translateY(0); }
    92.86% { transform: translateX(-50%) translateY(-14px); }
    100% { transform: translateX(-50%) translateY(0); }
  }
  @keyframes arrowBounceSouth {
    0% { transform: translateX(-50%) translateY(0); }
    28.57% { transform: translateX(-50%) translateY(56px); }
    57.14% { transform: translateX(-50%) translateY(0); }
    71.43% { transform: translateX(-50%) translateY(28px); }
    85.71% { transform: translateX(-50%) translateY(0); }
    92.86% { transform: translateX(-50%) translateY(14px); }
    100% { transform: translateX(-50%) translateY(0); }
  }
  @keyframes arrowBounceEast {
    0% { transform: translateY(-50%) translateX(0); }
    28.57% { transform: translateY(-50%) translateX(56px); }
    57.14% { transform: translateY(-50%) translateX(0); }
    71.43% { transform: translateY(-50%) translateX(28px); }
    85.71% { transform: translateY(-50%) translateX(0); }
    92.86% { transform: translateY(-50%) translateX(14px); }
    100% { transform: translateY(-50%) translateX(0); }
  }
  @keyframes arrowBounceWest {
    0% { transform: translateY(-50%) translateX(0); }
    28.57% { transform: translateY(-50%) translateX(-56px); }
    57.14% { transform: translateY(-50%) translateX(0); }
    71.43% { transform: translateY(-50%) translateX(-28px); }
    85.71% { transform: translateY(-50%) translateX(0); }
    92.86% { transform: translateY(-50%) translateX(-14px); }
    100% { transform: translateY(-50%) translateX(0); }
  }
`
if (!document.head.querySelector('[data-arrow-bounce-style]')) {
  style.setAttribute('data-arrow-bounce-style', 'true')
  document.head.appendChild(style)
}
